// src/pages/JudgeConsole.tsx
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  Check,
  ClipboardList,
  Leaf,
  Lock,
  LogOut,
  Music,
  PanelRight,
  Target,
  TriangleAlert,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { MARK, MARK_SCALE_ENDS, type SubjectType } from '@sdg/shared'
import { PortalFooter, PortalHeader } from '../components/PortalChrome'
import { Button } from '../components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../components/ui/dialog'
import { Note } from '../components/ui/card'
import { cn } from '../lib/cn'
import { api, get, post } from '../lib/api'
import { clearStaffSession, getStaffSession } from '../lib/staff'
import { TieBreakPanel, useTieStream } from './judge/TieBreakPanel'

/**
 * The Judge console — slides 18 to 22.
 * Laptop: subject list + open score sheet side by side (each scrolls on its own).
 * Phone / tablet: one panel at a time, with a way back to the list.
 */

interface SheetSummary {
  sheetId: number
  status: 'not_started' | 'in_progress' | 'submitted'
  locked: boolean
  rubric: { code: string; title: string }
  subject: { type: SubjectType; key: string; label: string; detail: string | null }
  marked: number
  totalKpis: number
  total: number | null
  submittedAt: string | null
}

interface SheetKpi {
  kpiId: number
  seq: number
  label: string
  weight: number
  mark: number | null
  score: number | null
}

interface SheetDetail {
  sheetId: number
  status: SheetSummary['status']
  locked: boolean
  rubric: { code: string; title: string; description: string | null }
  subject: SheetSummary['subject']
  kpis: SheetKpi[]
  marked: number
  totalKpis: number
  total: number
  submittedAt: string | null
  reopenedAt: string | null
}

interface Me {
  judge: { id: number; label: string; name: string | null }
  progress: { submitted: number; inProgress: number; total: number }
}

/**
 * Tab order and styling — **not** the list of tabs.
 *
 * Which tabs appear is decided by the sheets the server actually sends, and
 * this only says what they look like and in what order. It used to *be* the
 * list, and when the Committee withdrew the entrepreneurship award the tab
 * stayed: the rubric was gone from the database, its sheets were gone, and
 * the console still offered a judge an empty category to work through.
 *
 * A rubric with no entry here still gets a tab, using its own title — better
 * a plain tab than a missing one.
 */
const AWARD_STYLE = [
  { code: 'sdg_project', short: 'Best SDG Project', icon: Target },
  { code: 'sustainable_booth', short: 'Sustainable Booth', icon: Leaf },
  { code: 'cultural_performance', short: 'Cultural Performance', icon: Music },
] as const

/* ---------- shared styling ---------- */
const panel = 'rounded-2xl border border-white bg-white/95 shadow-lg shadow-blue-900/5'
const navyBtn =
  'h-11 rounded-xl bg-[#1B2A6B] px-5 py-0 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#141F55] disabled:opacity-50'
const outlineBtn =
  'h-11 rounded-xl border border-slate-200 bg-white py-0 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-60'

/**
 * The short tag in the subject list's circle.
 *
 * Digits alone for a school booth or a stage group, but the whole code for an
 * entrepreneurship booth: stripping the letter off "Booth E7" would leave a 7
 * **directly above the School of Computer Science's booth 7 in the same
 * list** — the two kinds of booth are judged on one rubric now, so they share
 * a tab, and that is the one mistake a judge on a busy floor must not be
 * invited to make.
 */
function subjectTag(subject: SheetSummary['subject']): string {
  if (subject.type === 'ent_booth') return subject.label.replace(/^Booth\s+/, '')
  return subject.label.replace(/\D+/g, '') || '•'
}

/** Colour for a mark: low = red, mid = amber, high = green. */
function markTone(n: number, max: number) {
  const r = n / max
  if (r <= 0.3) return 'border-red-500 bg-red-500 text-white shadow-red-500/30'
  if (r <= 0.5) return 'border-orange-500 bg-orange-500 text-white shadow-orange-500/30'
  if (r <= 0.7) return 'border-amber-400 bg-amber-400 text-white shadow-amber-400/30'
  return 'border-emerald-500 bg-emerald-500 text-white shadow-emerald-500/30'
}

/** 1–MARK.max picker. Full width on phones, compact beside the label on wide screens. */
function MarkPicker({
  value,
  onChange,
  disabled,
  labelledBy,
}: {
  value: number | null
  onChange: (v: number) => void
  disabled?: boolean
  labelledBy: string
}) {
  const marks = Array.from({ length: MARK.max }, (_, i) => i + 1)
  return (
    <div role="radiogroup" aria-labelledby={labelledBy} className="grid grid-cols-10 gap-1 sm:gap-1.5">
      {marks.map((n) => {
        const on = value === n
        return (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={on}
            disabled={disabled}
            onClick={() => onChange(n)}
            className={cn(
              'grid h-10 place-items-center rounded-lg border text-[13px] font-bold tabular-nums transition-all sm:h-9',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/40',
              on
                ? cn(markTone(n, MARK.max), 'scale-105 shadow-md')
                : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:bg-slate-50',
              disabled && 'cursor-not-allowed opacity-70',
              !disabled && !on && 'active:scale-95',
            )}
          >
            {n}
          </button>
        )
      })}
    </div>
  )
}

export default function JudgeConsole() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { id: routeSheetId } = useParams()

  const signedIn = !!getStaffSession()
  const [isConfirmingSignOut, setIsConfirmingSignOut] = useState(false)
  const [award, setAward] = useState<string>(AWARD_STYLE[0].code)
  const [openSheetId, setOpenSheetId] = useState<number | null>(
    routeSheetId ? Number(routeSheetId) : null,
  )

  const me = useQuery({
    queryKey: ['judge-me'],
    queryFn: () => get<Me>('/api/judge/me', 'staff'),
    enabled: signedIn,
  })

  const sheets = useQuery({
    queryKey: ['judge-sheets'],
    queryFn: () => get<{ sheets: SheetSummary[] }>('/api/judge/sheets', 'staff'),
    enabled: signedIn,
  })

  /*
   * Slide 22's last tie-break. Streamed rather than fetched, because a tie
   * can appear from another judge's final submission while this judge is
   * sitting on a finished console — and the ceremony is blocked until the
   * panel settles it.
   */
  const tieStream = useTieStream(signedIn)
  const ties = tieStream.data?.ties ?? []

  const all = sheets.data?.sheets ?? []

  /* A deep link to a sheet selects its award too, so the list shows it. */
  useEffect(() => {
    if (!openSheetId) return
    const found = all.find((s) => s.sheetId === openSheetId)
    if (found) setAward(found.rubric.code)
  }, [openSheetId, all])

  useEffect(() => {
    if (!signedIn) navigate('/staff/login', { replace: true })
  }, [signedIn, navigate])

  /**
   * One tab per rubric the judge actually has sheets for.
   *
   * Built from the sheets rather than from a list in this file, so a rubric
   * the Committee adds or withdraws moves the console with it. `AWARD_STYLE`
   * supplies the order and the icon; anything it does not know about is
   * appended using the rubric's own title.
   */
  const awards = useMemo(() => {
    const present = new Map<string, string>()
    for (const s of all) present.set(s.rubric.code, s.rubric.title)

    const styled = AWARD_STYLE.filter((a) => present.has(a.code)).map((a) => ({
      code: a.code as string,
      short: a.short as string,
      icon: a.icon,
    }))

    const known = new Set(styled.map((a) => a.code))
    const rest = [...present]
      .filter(([code]) => !known.has(code))
      .map(([code, title]) => ({ code, short: title, icon: Target }))

    return [...styled, ...rest]
  }, [all])

  /*
   * Follow the tabs when they change. The selected code is held in state and
   * seeded with a guess, which is wrong if that rubric has no sheets — the
   * judge would open the console on an empty category with no indication why.
   */
  useEffect(() => {
    if (awards.length > 0 && !awards.some((a) => a.code === award)) setAward(awards[0].code)
  }, [awards, award])

  const forAward = useMemo(() => all.filter((s) => s.rubric.code === award), [all, award])
  const submitted = all.filter((s) => s.status === 'submitted').length
  const pct = all.length ? (submitted / all.length) * 100 : 0

  async function signOut() {
    await post('/api/staff/logout', {}, 'staff').catch(() => { })
    clearStaffSession()
    queryClient.clear()
    navigate('/staff/login', { replace: true })
  }

  const activeAward = awards.find((a) => a.code === award)

  return (
    <div className="judge-console flex h-dvh select-none flex-col overflow-hidden bg-gradient-to-br from-[#BFD8FA] via-[#E9F1FD] to-[#F5EFFC] font-sans text-[#0B1E3F] [-webkit-tap-highlight-color:transparent]">
      <PortalHeader
        portal="Judge portal"
        identity={me.data?.judge.name ?? me.data?.judge.label ?? 'Judge'}
        details={me.data?.judge.label ? 'Scoring account' : 'Judge account'}
        rightContent={
          <>
            <div className="hidden w-36 shrink-0 sm:block">
              <div className="flex items-baseline justify-between text-[9px] font-semibold uppercase tracking-[0.1em] text-slate-500">
                Submitted
                <span className="text-xs normal-case tracking-normal text-[#173B66] tabular-nums">
                  {submitted}
                  <span className="text-slate-400">/{all.length || '—'}</span>
                </span>
              </div>
              <div className="mt-1 h-1 overflow-hidden rounded-full bg-slate-200/90">
                <div
                  className="h-full rounded-full bg-[#4D9A83] transition-[width] duration-500"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
            <span className="shrink-0 rounded-md border border-emerald-100 bg-white/75 px-2.5 py-1 text-xs font-semibold text-emerald-700 tabular-nums sm:hidden">
              {submitted}/{all.length || '—'}
            </span>
          </>
        }
        onSignOut={() => setIsConfirmingSignOut(true)}
      />

      {/* ───────── Award tabs ───────── */}
      <nav className="shrink-0 px-3 pt-3 sm:px-5" aria-label="Award categories">
        <div className="mx-auto flex max-w-7xl gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {awards.map((a) => {
            const mine = all.filter((s) => s.rubric.code === a.code)
            const done = mine.filter((s) => s.status === 'submitted').length
            const active = a.code === award
            const Icon = a.icon
            return (
              <button
                key={a.code}
                type="button"
                onClick={() => {
                  setAward(a.code)
                  setOpenSheetId(null)
                  navigate('/judge', { replace: true })
                }}
                className={cn(
                  'flex shrink-0 items-center gap-2 rounded-xl border px-3.5 py-2 text-left text-[13px] font-semibold transition-colors sm:flex-1 sm:justify-center',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/40',
                  active
                    ? 'border-[#1B2A6B] bg-[#1B2A6B] text-white shadow-md shadow-blue-900/20'
                    : 'border-white bg-white/80 text-slate-600 hover:bg-white',
                )}
              >
                <Icon className={cn('size-4', active ? 'text-amber-400' : 'text-slate-400')} />
                {a.short}
                {mine.length > 0 && (
                  <span
                    className={cn(
                      'rounded-full px-2 py-0.5 text-[10.5px] font-bold tabular-nums',
                      active ? 'bg-white/15 text-white' : 'bg-slate-100 text-slate-500',
                    )}
                  >
                    {done}/{mine.length}
                  </span>
                )}
              </button>
            )
          })}
        </div>
      </nav>

      {/* ───────── Panels ───────── */}
      <main className="mx-auto flex min-h-0 w-full max-w-7xl flex-1 gap-3 p-3 sm:p-5 sm:pt-3">
        {/* Subject list */}
        <section
          className={cn(
            panel,
            'min-h-0 w-full shrink-0 flex-col overflow-hidden lg:flex lg:w-72 xl:w-80',
            openSheetId ? 'hidden' : 'flex',
          )}
        >
          <div className="shrink-0 border-b border-slate-100 px-4 py-3">
            <p className="text-sm font-bold text-[#0B1E3F]">{activeAward?.short}</p>
            <p className="text-xs text-slate-500">
              {forAward.length} to score ·{' '}
              {forAward.filter((s) => s.status === 'submitted').length} submitted
            </p>
          </div>

          <ul className="min-h-0 flex-1 space-y-1 overflow-y-auto p-2 custom-scrollbar">
            {sheets.isLoading && (
              <li className="px-2 py-8 text-center text-sm text-slate-500">Loading…</li>
            )}

            {forAward.map((sheet) => {
              const open = sheet.sheetId === openSheetId
              return (
                <li key={sheet.sheetId}>
                  <button
                    type="button"
                    onClick={() => {
                      setOpenSheetId(sheet.sheetId)
                      navigate(`/judge/sheet/${sheet.sheetId}`, { replace: true })
                    }}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-xl border px-2.5 py-2.5 text-left transition-colors',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/40',
                      open
                        ? 'border-orange-300 bg-orange-50'
                        : 'border-transparent hover:bg-slate-50',
                    )}
                  >
                    <span
                      className={cn(
                        'grid size-9 shrink-0 place-items-center rounded-full text-xs font-bold tabular-nums',
                        sheet.status === 'submitted'
                          ? 'bg-emerald-500 text-white'
                          : sheet.status === 'in_progress'
                            ? 'bg-amber-100 text-amber-700 ring-1 ring-amber-300'
                            : 'bg-slate-100 text-slate-400',
                      )}
                    >
                      {sheet.status === 'submitted' ? (
                        <Check className="size-4" strokeWidth={3} />
                      ) : (
                        subjectTag(sheet.subject)
                      )}
                    </span>

                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13px] font-bold leading-tight text-[#0B1E3F]">
                        {sheet.subject.label}
                      </span>
                      {sheet.subject.detail && (
                        <span className="block truncate text-[11.5px] leading-tight text-slate-500">
                          {sheet.subject.detail}
                        </span>
                      )}
                    </span>

                    {sheet.status === 'submitted' ? (
                      <span className="shrink-0 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-bold tabular-nums text-emerald-700">
                        {sheet.total?.toFixed(1)}
                      </span>
                    ) : sheet.marked > 0 ? (
                      <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold tabular-nums text-amber-700">
                        {sheet.marked}/{sheet.totalKpis}
                      </span>
                    ) : null}
                  </button>
                </li>
              )
            })}
          </ul>
        </section>

        {/* Open score sheet */}
        <section
          className={cn(
            panel,
            'min-h-0 min-w-0 flex-1 flex-col overflow-hidden lg:flex',
            openSheetId ? 'flex' : 'hidden',
          )}
        >
          {openSheetId ? (
            <ScoreSheet
              key={openSheetId}
              sheetId={openSheetId}
              onBack={() => {
                setOpenSheetId(null)
                navigate('/judge', { replace: true })
              }}
              onSubmitted={() => {
                // Move straight to the next unsubmitted subject in this award.
                const rest = forAward.filter(
                  (s) => s.sheetId !== openSheetId && s.status !== 'submitted',
                )
                const next = rest[0]
                if (next) {
                  setOpenSheetId(next.sheetId)
                  navigate(`/judge/sheet/${next.sheetId}`, { replace: true })
                }
              }}
            />
          ) : (
            <EmptyPanel count={forAward.length} />
          )}
        </section>
      </main>

      <PortalFooter />

      {/* ───────── Confirm sign out ───────── */}
      <Dialog open={isConfirmingSignOut} onOpenChange={setIsConfirmingSignOut}>
        <DialogContent className="max-h-[92dvh] overflow-y-auto rounded-3xl p-0" showClose={false}>
          <div className="px-6 pb-6 pt-7">
            <div className="text-center">
              <span className="mx-auto mb-3 grid size-12 place-items-center rounded-full bg-[#1B2A6B] text-white">
                <LogOut className="size-6" strokeWidth={2.4} />
              </span>
              <DialogTitle className="text-lg font-bold text-[#0B1E3F]">
                Sign out?
              </DialogTitle>
              <DialogDescription className="mt-1.5 text-sm text-slate-500">
                You can sign back in at any time to carry on scoring.
              </DialogDescription>
            </div>

            <Note className="mt-4 rounded-xl border-blue-100 bg-blue-50/80 text-xs text-slate-600">
              Your saved marks are kept. {submitted} of {all.length || '—'} sheets submitted.
            </Note>

            <div className="mt-4 flex gap-2.5">
              <Button
                block
                autoFocus
                onClick={() => setIsConfirmingSignOut(false)}
                className={outlineBtn}
              >
                Stay signed in
              </Button>
              <Button
                block
                onClick={signOut}
                className="h-11 rounded-xl bg-[#1B2A6B] py-0 text-sm font-semibold text-white shadow-sm hover:bg-[#141F55] disabled:opacity-60"
              >
                Sign out
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/*
        Last, so it sits over everything including the sign-out dialog. The
        podium cannot be published while a tie stands, so this is the most
        urgent thing on the judge's screen whenever it appears.
      */}
      {me.data && <TieBreakPanel judgeId={me.data.judge.id} ties={ties} />}
    </div>
  )
}

function EmptyPanel({ count }: { count: number }) {
  return (
    <div className="grid min-h-0 flex-1 place-items-center p-8">
      <div className="max-w-sm text-center">
        <span className="mx-auto mb-4 grid size-16 place-items-center rounded-full bg-blue-50 text-blue-500">
          <PanelRight className="size-7" strokeWidth={1.8} />
        </span>
        <p className="text-base font-bold text-[#0B1E3F]">Pick a subject to score</p>
        <p className="mt-1.5 text-sm leading-snug text-slate-500">
          {count} in this award. Each sheet has 7 KPIs marked 1–{MARK.max}; the weighted total is
          worked out as you go.
        </p>
        <p className="mt-3 text-xs">
          <span style={{ color: MARK_SCALE_ENDS.low.colour }} className="font-bold">
            {MARK_SCALE_ENDS.low.text}
          </span>
          <span className="text-slate-300"> · </span>
          <span style={{ color: MARK_SCALE_ENDS.high.colour }} className="font-bold">
            {MARK_SCALE_ENDS.high.text}
          </span>
        </p>
      </div>
    </div>
  )
}

// ── The score sheet panel ─────────────────────────────────────

function ScoreSheet({
  sheetId,
  onBack,
  onSubmitted,
}: {
  sheetId: number
  /** Returns to the subject list. Only visible below `lg`. */
  onBack: () => void
  onSubmitted: () => void
}) {
  const queryClient = useQueryClient()

  const [confirming, setConfirming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState<Record<number, number>>({})
  const [saving, setSaving] = useState(false)
  const [savedAt, setSavedAt] = useState<number | null>(null)

  const sheet = useQuery({
    queryKey: ['judge-sheet', sheetId],
    queryFn: () => get<{ sheet: SheetDetail }>(`/api/judge/sheets/${sheetId}`, 'staff'),
    refetchOnWindowFocus: false,
  })

  const save = useMutation({
    mutationFn: (marks: { kpiId: number; mark: number }[]) =>
      api<{ sheet: SheetDetail }>(`/api/judge/sheets/${sheetId}`, {
        method: 'PATCH',
        body: JSON.stringify({ marks }),
        auth: 'staff',
      }),
    onSuccess: (res) => {
      queryClient.setQueryData(['judge-sheet', sheetId], { sheet: res.sheet })
      queryClient.invalidateQueries({ queryKey: ['judge-sheets'] })
      setSavedAt(Date.now())
      setError(null)
    },
    onError: () => setError('That mark did not save. Tap it again.'),
  })

  const submit = useMutation({
    mutationFn: () =>
      api<{ sheet: SheetDetail }>(`/api/judge/sheets/${sheetId}/submit`, {
        method: 'POST',
        auth: 'staff',
      }),
    onSuccess: (res) => {
      queryClient.setQueryData(['judge-sheet', sheetId], { sheet: res.sheet })
      queryClient.invalidateQueries({ queryKey: ['judge-sheets'] })
      queryClient.invalidateQueries({ queryKey: ['judge-me'] })
      setConfirming(false)
      onSubmitted()
    },
    onError: () => {
      setConfirming(false)
      setError('Could not submit. Check every KPI is marked and try again.')
    },
  })

  /*
   * Taps are batched briefly before being sent. Marking seven KPIs quickly
   * would otherwise fire seven requests that race, and the last reply to
   * arrive — not the last tap — would win.
   */
  const queued = useRef<Record<number, number>>({})
  const timer = useRef<number | null>(null)

  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current) }, [])

  function setMark(kpiId: number, mark: number) {
    setPending((prev) => ({ ...prev, [kpiId]: mark }))
    queued.current[kpiId] = mark
    setSaving(true)

    if (timer.current) window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => {
      const batch = Object.entries(queued.current).map(([kpi, value]) => ({
        kpiId: Number(kpi),
        mark: value,
      }))
      queued.current = {}
      if (batch.length === 0) return setSaving(false)

      save
        .mutateAsync(batch)
        .catch(() => { })
        .finally(() => {
          setSaving(false)
          setPending({})
        })
    }, 260)
  }

  if (sheet.isLoading) {
    return (
      <div className="grid min-h-0 flex-1 place-items-center">
        <div className="grid place-items-center gap-3">
          <span className="size-8 animate-spin rounded-full border-[3px] border-slate-200 border-t-orange-500" />
          <p className="text-sm text-slate-500">Loading the score sheet…</p>
        </div>
      </div>
    )
  }

  if (sheet.isError || !sheet.data) {
    return (
      <div className="grid min-h-0 flex-1 place-items-center p-8">
        <Note tone="error" className="max-w-sm rounded-xl text-center">
          This sheet is not one of yours, or the server did not respond.
        </Note>
      </div>
    )
  }

  const s = sheet.data.sheet
  const markOf = (kpi: SheetKpi) => pending[kpi.kpiId] ?? kpi.mark

  const markedCount = s.kpis.filter((k) => markOf(k) != null).length
  const runningTotal = s.kpis.reduce((sum, kpi) => {
    const mark = markOf(kpi)
    return mark == null ? sum : sum + (mark / MARK.max) * kpi.weight
  }, 0)
  const complete = markedCount === s.kpis.length

  return (
    <>
      {/* Header stays put; only the KPI list below it scrolls. */}
      <div className="shrink-0 border-b border-slate-100 px-4 py-3 sm:px-5">
        <button
          type="button"
          onClick={onBack}
          className="mb-2 inline-flex items-center gap-1 text-xs font-bold text-orange-600 hover:underline lg:hidden"
        >
          <ArrowLeft className="size-3.5" strokeWidth={2.4} />
          All subjects
        </button>

        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-orange-600">
              {s.rubric.title}
            </p>
            <h2 className="mt-0.5 truncate text-xl font-extrabold leading-tight text-[#0B1E3F]">
              {s.subject.label}
            </h2>
            {s.subject.detail && (
              <p className="truncate text-[13px] text-slate-500">{s.subject.detail}</p>
            )}
          </div>

          <div
            className={cn(
              'shrink-0 rounded-2xl px-4 py-2 text-center transition-colors',
              complete
                ? 'bg-gradient-to-br from-[#2B3E9A] to-[#141F55] text-white shadow-md shadow-blue-900/20'
                : 'bg-slate-100 text-slate-500',
            )}
          >
            <p
              className={cn(
                'text-[9px] font-bold uppercase tracking-[0.16em]',
                complete ? 'text-blue-200' : 'text-slate-400',
              )}
            >
              Total
            </p>
            <p className="text-2xl font-black leading-none tabular-nums">
              {runningTotal.toFixed(1)}
            </p>
            <p className={cn('text-[9px]', complete ? 'text-blue-200/80' : 'text-slate-400')}>
              of 100
            </p>
          </div>
        </div>

        {/* marked progress */}
        <div className="mt-3 flex items-center gap-3">
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-200">
            <div
              className="h-full rounded-full bg-gradient-to-r from-amber-400 to-orange-500 transition-[width] duration-300"
              style={{ width: `${s.kpis.length ? (markedCount / s.kpis.length) * 100 : 0}%` }}
            />
          </div>
          <span className="text-xs font-bold tabular-nums text-slate-500">
            {markedCount}/{s.kpis.length} marked
          </span>
        </div>
      </div>

      {/* The one scrolling region. */}
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3 sm:px-5">
        {s.locked && (
          <Note tone="green" className="mb-3 flex items-start gap-2.5 rounded-xl text-sm">
            <Lock className="mt-px size-4 shrink-0" strokeWidth={2.2} />
            <span>
              Submitted and locked
              {s.submittedAt &&
                ` at ${new Date(s.submittedAt).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}`}
              . Only Admin can re-open it.
            </span>
          </Note>
        )}

        {s.reopenedAt && !s.locked && (
          <Note className="mb-3 flex items-start gap-2.5 rounded-xl border-amber-200 bg-amber-50 text-sm text-amber-800">
            <TriangleAlert className="mt-px size-4 shrink-0" strokeWidth={2.2} />
            <span>Admin re-opened this sheet. Review your marks and submit it again.</span>
          </Note>
        )}

        {/* Scale legend, once per sheet */}
        <div className="mb-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 rounded-xl border border-blue-100 bg-blue-50/70 px-3.5 py-2">
          <p className="text-xs font-bold text-[#0B1E3F]">
            Mark every KPI from 1 to {MARK.max}
          </p>
          <p className="text-[11px] font-bold">
            <span style={{ color: MARK_SCALE_ENDS.low.colour }}>{MARK_SCALE_ENDS.low.text}</span>
            <span className="text-slate-300"> · </span>
            <span style={{ color: MARK_SCALE_ENDS.high.colour }}>{MARK_SCALE_ENDS.high.text}</span>
          </p>
        </div>

        <ol className="space-y-2.5 pb-1">
          {s.kpis.map((kpi) => {
            const mark = markOf(kpi)
            const score = mark == null ? null : (mark / MARK.max) * kpi.weight

            return (
              <li
                key={kpi.kpiId}
                className={cn(
                  'rounded-xl border bg-white p-3 transition-colors',
                  mark == null ? 'border-slate-200' : 'border-emerald-200 bg-emerald-50/30',
                )}
              >
                <div className="flex items-start gap-3">
                  <span
                    className={cn(
                      'grid size-7 shrink-0 place-items-center rounded-full text-xs font-bold tabular-nums',
                      mark == null ? 'bg-slate-100 text-slate-400' : 'bg-emerald-500 text-white',
                    )}
                  >
                    {mark == null ? kpi.seq : <Check className="size-3.5" strokeWidth={3} />}
                  </span>

                  <p
                    id={`kpi-${kpi.kpiId}`}
                    className="min-w-0 flex-1 text-[13.5px] font-semibold leading-snug text-[#0B1E3F]"
                  >
                    {kpi.label}
                  </p>

                  <span className="shrink-0 text-right leading-none">
                    <span className="block text-[9.5px] font-bold uppercase tracking-wider text-slate-400">
                      Weight {kpi.weight}
                    </span>
                    <span
                      className={cn(
                        'mt-1 block text-sm font-extrabold tabular-nums',
                        score == null ? 'text-slate-300' : 'text-orange-600',
                      )}
                    >
                      {score == null ? '—' : score.toFixed(1)}
                    </span>
                  </span>
                </div>

                <div className="mt-3">
                  <MarkPicker
                    value={mark}
                    onChange={(value) => setMark(kpi.kpiId, value)}
                    disabled={s.locked}
                    labelledBy={`kpi-${kpi.kpiId}`}
                  />
                </div>
              </li>
            )
          })}
        </ol>

        {error && (
          <Note tone="error" className="mt-3 rounded-xl text-sm">
            {error}
          </Note>
        )}
      </div>

      {/* Action bar, pinned to the foot of the panel. */}
      {!s.locked && (
        <div className="flex shrink-0 flex-col gap-2 border-t border-slate-100 bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <p className="text-xs text-slate-500">
            {complete
              ? 'Review the marks, then submit. Submitting locks the sheet.'
              : `Mark all ${s.kpis.length} KPIs to submit.`}
            <span
              className={cn(
                'ml-2 font-bold',
                saving ? 'text-amber-600' : savedAt ? 'text-emerald-600' : 'text-transparent',
              )}
              aria-live="polite"
            >
              {saving ? 'Saving…' : savedAt ? 'Saved' : '·'}
            </span>
          </p>

          <Button
            disabled={!complete || saving || submit.isPending}
            onClick={() => setConfirming(true)}
            className={cn(navyBtn, 'w-full sm:w-auto')}
          >
            <ClipboardList className="size-4" strokeWidth={2.2} />
            Review and submit
          </Button>
        </div>
      )}

      {/* "Each score sheet is reviewed, then submitted and locked" */}
      <Dialog open={confirming} onOpenChange={setConfirming}>
        <DialogContent className="max-h-[92dvh] overflow-y-auto rounded-3xl p-0" showClose={false}>
          <div className="px-6 pb-6 pt-7">
            <div className="text-center">
              <span className="mx-auto mb-3 grid size-12 place-items-center rounded-full bg-[#1B2A6B] text-white">
                <Check className="size-6" strokeWidth={2.6} />
              </span>
              <DialogTitle className="text-lg font-bold text-[#0B1E3F]">
                Submit this score sheet?
              </DialogTitle>
              <DialogDescription className="mt-1.5 text-sm text-slate-500">
                {s.rubric.title} · {s.subject.label}
              </DialogDescription>
            </div>

            <div className="mt-4 rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3 text-center">
              <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">
                Your total
              </p>
              <p className="text-3xl font-black leading-none tabular-nums text-[#0B1E3F]">
                {runningTotal.toFixed(1)}
              </p>
              <p className="text-[11px] text-slate-400">of 100</p>
            </div>

            <ul className="mt-3 divide-y divide-slate-100 text-xs">
              {s.kpis.map((kpi) => (
                <li key={kpi.kpiId} className="flex items-center justify-between gap-3 py-2">
                  <span className="min-w-0 flex-1 truncate text-slate-500">{kpi.label}</span>
                  <span className="shrink-0 font-bold tabular-nums text-[#0B1E3F]">
                    {markOf(kpi)} × {kpi.weight}
                  </span>
                </li>
              ))}
            </ul>

            <Note className="mt-3 rounded-xl border-blue-100 bg-blue-50/80 text-xs text-slate-600">
              Once submitted, this sheet is locked. Only Admin can re-open it.
            </Note>

            <div className="mt-4 flex gap-2.5">
              <Button
                block
                disabled={submit.isPending}
                onClick={() => setConfirming(false)}
                autoFocus
                className={outlineBtn}
              >
                Keep editing
              </Button>
              <Button
                block
                disabled={submit.isPending}
                onClick={() => submit.mutate()}
                className="h-11 rounded-xl bg-orange-500 py-0 text-sm font-semibold text-white shadow-sm hover:bg-orange-600 disabled:opacity-60"
              >
                {submit.isPending ? 'Submitting…' : 'Submit and lock'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
